import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { Collection } from "@shared/schemas/common";
import type {
  CreateScenarioRequest,
  CreateShareRequest,
  PatchScenarioRequest,
  SaveModelRequest,
  ScenarioDetail,
  ScenarioRow,
} from "@shared/schemas/scenario";
import { queryKeys } from "@client/lib/queryClient";
import {
  archiveScenario,
  createScenario,
  duplicateScenario,
  getScenario,
  listScenarios,
  patchScenario,
  saveScenarioModel,
  shareScenario,
  unshareScenario,
  type ScenarioListFilters,
} from "@client/lib/scenariosApi";

/**
 * Scenario reads and writes as hooks.
 *
 * Anything that changes a scenario also changes its product's row — the
 * scenario counts, the baseline pointer, the "last activity" a card shows — so
 * these invalidate both trees rather than guessing which one the user is
 * looking at.
 */

function useScenarioWriteback() {
  const client = useQueryClient();
  return (scenario: ScenarioDetail) => {
    client.setQueryData(queryKeys.scenarios.detail(scenario.id), scenario);
    void client.invalidateQueries({ queryKey: queryKeys.scenarios.all });
    void client.invalidateQueries({ queryKey: queryKeys.products.all });
  };
}

export function useScenarios(
  productId: string | undefined,
  filters: ScenarioListFilters,
): UseQueryResult<Collection<ScenarioRow>> {
  return useQuery({
    queryKey: queryKeys.scenarios.list(productId ?? "", filters),
    queryFn: () => listScenarios(productId!, filters),
    enabled: Boolean(productId),
    placeholderData: (previous) => previous,
  });
}

export function useScenario(id: string | undefined): UseQueryResult<ScenarioDetail> {
  return useQuery({
    queryKey: queryKeys.scenarios.detail(id ?? ""),
    queryFn: () => getScenario(id!),
    enabled: Boolean(id),
  });
}

export function useCreateScenario(productId: string) {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: (input: CreateScenarioRequest) => createScenario(productId, input),
    onSuccess: writeBack,
  });
}

export function useDuplicateScenario() {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => duplicateScenario(id, name),
    onSuccess: writeBack,
  });
}

export function useArchiveScenario() {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: ({ id, restore = false }: { id: string; restore?: boolean }) =>
      archiveScenario(id, restore),
    onSuccess: writeBack,
  });
}

export function usePatchScenario() {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PatchScenarioRequest }) =>
      patchScenario(id, input),
    onSuccess: writeBack,
  });
}

/**
 * The editor's save. The response carries the stored scenario, so the detail
 * cache is written from it rather than invalidated — the editor is looking at
 * that exact query, and refetching it would put a loading skeleton over a page
 * whose contents we already hold.
 */
export function useSaveScenarioModel(id: string) {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: (input: SaveModelRequest) => saveScenarioModel(id, input),
    onSuccess: (response) => writeBack(response.scenario),
  });
}

export function useShareScenario(id: string) {
  const writeBack = useScenarioWriteback();
  return useMutation({
    mutationFn: (input: CreateShareRequest) => shareScenario(id, input),
    onSuccess: writeBack,
  });
}

export function useUnshareScenario(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => unshareScenario(id, userId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.scenarios.all });
      void client.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}
